import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    model,
    output,
    signal,
    viewChild,
    WritableSignal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Bind } from "../../decorators/bind.decorator";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25DatePatternModule } from "../../modules/s25-datepattern/s25.datepattern.module";
import { S25DatePatternPickerComponent } from "../../modules/s25-datepattern/s25.datepattern.picker.component";
import { S25EditableModule } from "../../modules/s25-editable/s25.editable.module";
import { MultiselectModelI } from "../../modules/s25-multiselect/s25.multiselect.component";
import { S25MultiselectModule } from "../../modules/s25-multiselect/s25-multiselect.module";
import { Table } from "../../modules/s25-table/Table";
import { S25TableComponent } from "../../modules/s25-table/s25.table.component";
import { S25TableModule } from "../../modules/s25-table/s25.table.module";
import { Event } from "../../pojo/Event";
import { ProfileApiI } from "../../pojo/ProfileI";
import { S25ItemI } from "../../pojo/S25ItemI";
import { AlertService } from "../../services/alert.service";
import { UserStore } from "../../stores/user.store";
import { S25Util } from "../../util/s25-util";
import { S25Datefilter } from "../../modules/s25-dateformat/s25.datefilter.service";
import { ProfileUtil } from "../../modules/s25-datepattern/profile.util";
import { Item } from "../../pojo/Item";
import {
    DEFAULT_OCCURRENCE_SCHEDULE_CONFIG,
    OccurrenceScheduleConfig,
    OccurrenceScheduleDatesChangeContext,
    OccurrenceScheduleDatesChangeHandler,
    OccurrenceScheduleObject,
    OccurrenceScheduleObjectChangeContext,
    OccurrenceScheduleObjectChangeHandler,
    OccurrenceScheduleRow,
    OccurrenceScheduleValue,
    scheduleItemFromObject,
    scheduleObjectFromItem,
} from "./occurrence-object-schedule.types";
import {
    buildScheduleValue,
    createDefaultScheduleScenario,
    expandOccurrenceRows,
    mergeDefaultAndPerOccurrence,
    objectsMatch,
} from "./occurrence-object-schedule.util";
import { S25OccurrenceScheduleLocationsCellComponent } from "./s25.occurrence.schedule.locations.cell.component";
import Occurrence = Event.Occurrence;

@TypeManagerDecorator("s25-ng-occurrence-object-schedule")
@Component({
    selector: "s25-ng-occurrence-object-schedule",
    host: { class: "occurrence-object-schedule-host" },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, S25EditableModule, S25DatePatternModule, S25MultiselectModule, S25TableModule],
    template: `
        <section class="occurrence-object-schedule">
            <div class="occurrence-object-schedule__section">
                <h3 class="occurrence-object-schedule__heading">Occurrence date and time</h3>
                <s25-ng-editable-start-end-datetime
                    [startDatetime]="scenarioStart()"
                    (startDatetimeChange)="onScenarioStartChange($event)"
                    [endDatetime]="scenarioEnd()"
                    (endDatetimeChange)="onScenarioEndChange($event)"
                    [alwaysEditing]="true"
                    [noTimeUpdated]="true"
                />
                <s25-ng-date-pattern-picker
                    [timeModel]="timeModel()"
                    [profileCode]="profileCode()"
                    [(adHocOccs)]="adHocOccs"
                    (adHocOccsChange)="onAdHocOccsChange()"
                    [(apiBean)]="datePatternApi"
                    (apiBeanChange)="onDatePatternChange()"
                    [minDate]="scenarioStart()"
                    [validatePattern]="validateAdHocOccDate"
                    [includeFirstDate]="true"
                />
            </div>

            @if (resolvedConfig().showAllOccurrencesPicker && showLocations()) {
                <div class="occurrence-object-schedule__section">
                    <h3 class="occurrence-object-schedule__heading">Locations for all occurrences</h3>
                    <p class="occurrence-object-schedule__helper ngFinePrint">
                        Same locations on every generated date. You can change individual dates below.
                    </p>
                    <s25-ng-multiselect-search-criteria
                        [type]="'locations'"
                        [buttonText]="'Select locations'"
                        [modelBean]="allOccurrencesPickerBean"
                        [selectedItems]="allOccurrencesItems()"
                        [showResults]="true"
                        [popoverOnBody]="true"
                        (close)="onAllOccurrencesPickerClose($event)"
                        (changed)="onAllOccurrencesSelectionChanged($event)"
                    />
                </div>
            }

            @if (resolvedConfig().showPerOccurrenceRows && showLocations() && tableReady()) {
                <div class="occurrence-object-schedule__section">
                    <h3 class="occurrence-object-schedule__heading">By occurrence</h3>
                    <s25-ng-table
                        #occurrenceTable
                        [dataSource]="tableDataSource"
                        [hasFilter]="false"
                        [hasRefresh]="false"
                        [hasColumnChooser]="false"
                        [columnSortable]="false"
                    />
                </div>
            }
        </section>
    `,
    styles: `
        .occurrence-object-schedule {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .occurrence-object-schedule__section {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .occurrence-object-schedule__heading {
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
        }

        .occurrence-object-schedule__helper {
            margin: 0;
        }
    `,
})
export class S25OccurrenceObjectScheduleComponent {
    readonly model = model<OccurrenceScheduleValue | undefined>(undefined);
    readonly config = input<Partial<OccurrenceScheduleConfig>>({});
    readonly afterDatesChange = input<OccurrenceScheduleDatesChangeHandler | undefined>(undefined);
    readonly afterObjectChange = input<OccurrenceScheduleObjectChangeHandler | undefined>(undefined);

    readonly datesChange = output<OccurrenceScheduleDatesChangeContext>();
    readonly objectChange = output<OccurrenceScheduleObjectChangeContext>();

    readonly datePicker = viewChild(S25DatePatternPickerComponent);
    readonly occurrenceTable = viewChild<S25TableComponent>("occurrenceTable");

    readonly allOccurrencesPickerBean: MultiselectModelI = { selectedItems: [] };

    private readonly defaultScenario = createDefaultScheduleScenario();

    readonly scenarioStart = signal(this.defaultScenario.startDt);
    readonly scenarioEnd = signal(this.defaultScenario.endDt);
    readonly adHocOccs = model<Occurrence[]>([
        { evStartDt: this.defaultScenario.startDt, evEndDt: this.defaultScenario.endDt },
    ]);
    readonly profileCode = signal("dnr");
    readonly objectsForAllOccurrences = signal<OccurrenceScheduleObject[]>([]);
    readonly perOccurrenceOverrides = signal<Map<number, OccurrenceScheduleObject[]>>(new Map());
    readonly customizedOccIds = signal<Set<number>>(new Set());
    readonly expandedOccurrences = signal<OccurrenceScheduleRow[]>([
        { occId: 1, startDt: this.defaultScenario.startDt, endDt: this.defaultScenario.endDt },
    ]);
    readonly tableRevision = signal(0);

    private readonly userStore = inject(UserStore);

    datePatternApi: ProfileApiI = {};

    readonly rowPickerBeans = new Map<number, MultiselectModelI>();
    private readonly rowSelectedItems = new Map<number, WritableSignal<S25ItemI[]>>();
    private readonly rowIsCustomized = new Map<number, WritableSignal<boolean>>();
    private readonly rowLocationCells = new Map<number, Table.Cell>();
    private readonly lastSyncedRowSelection = new Map<number, S25ItemI[]>();

    readonly resolvedConfig = computed(
        (): OccurrenceScheduleConfig => ({
            ...DEFAULT_OCCURRENCE_SCHEDULE_CONFIG,
            ...this.config(),
        }),
    );

    readonly showLocations = computed(() => this.resolvedConfig().objectKinds.includes("locations"));

    readonly timeModel = computed(() => ({
        evStartDt: this.scenarioStart(),
        evEndDt: S25Util.date.copyTime(new Date(this.scenarioStart()), this.scenarioEnd()),
        minutes: { setupMinutes: 0, preMinutes: 0, postMinutes: 0, takedownMinutes: 0 },
    }));

    readonly allOccurrencesItems = computed(() =>
        this.objectsForAllOccurrences().map((scheduleObject) => scheduleItemFromObject(scheduleObject)),
    );

    readonly tableReady = computed(() => Boolean(this.userStore.dateTimeFormat()));

    readonly validateAdHocOccDate = (date: Date) => date >= S25Util.date.toStartOfDay(this.scenarioStart());

    readonly tableDataSource: Table.DataSource = {
        type: "unpaginated",
        dataSource: this.getTableRows,
        columns: [
            { id: "start", header: "Start", minWidth: 140, type: "date", sortable: true },
            { id: "end", header: "End", minWidth: 140, type: "date", sortable: true },
            {
                id: "locations",
                header: "Locations",
                cells: { component: S25OccurrenceScheduleLocationsCellComponent },
                sortable: false,
            },
        ],
    };

    constructor() {
        this.hydrateFromModel(this.model());
        afterNextRender(() => {
            this.refreshExpandedOccurrences(false);
            this.updateModel();
        });
    }

    onScenarioStartChange(startDt: Date): void {
        const durationMinutes = S25Util.date.diffMinutes(this.scenarioStart(), this.scenarioEnd());
        this.scenarioStart.set(startDt);
        this.scenarioEnd.set(S25Util.date.addMinutes(S25Util.date.clone(startDt), durationMinutes));
    }

    onScenarioEndChange(endDt: Date): void {
        this.scenarioEnd.set(endDt);
        this.syncPickerTimeModelFromScenario();
        const profile = this.datePicker()?.getProfileModel();
        if (profile?.type === "adhoc" || (this.profileCode() && this.profileCode() !== "dnr")) {
            this.datePicker()?.changeFirstDt(this.scenarioStart());
        }
        this.syncAdHocOccsFromPicker();
        this.refreshExpandedOccurrences(true, "endChange");
    }

    onAdHocOccsChange(): void {
        this.refreshExpandedOccurrences(true, "patternChange");
    }

    onDatePatternChange(): void {
        const nextProfileCode = this.datePicker()?.getProfileCode() ?? this.datePatternApi.getProfileCode?.() ?? "dnr";
        this.profileCode.set(nextProfileCode);
        this.refreshExpandedOccurrences(true, "patternChange");
    }

    onAllOccurrencesSelectionChanged(selectedItems: S25ItemI[]): void {
        const nextObjects = S25Util.array
            .forceArray(selectedItems)
            .map((item) => scheduleObjectFromItem(item, Item.Ids.Location));
        const previousObjects = this.objectsForAllOccurrences();
        if (objectsMatch(nextObjects, previousObjects)) {
            return;
        }

        const previousIds = new Set(previousObjects.map((object) => object.itemId));
        const nextIds = new Set(nextObjects.map((object) => object.itemId));
        const removedIds = [...previousIds].filter((itemId) => !nextIds.has(itemId));
        if (removedIds.length) {
            const nextOverrides = new Map(this.perOccurrenceOverrides());
            for (const occId of this.customizedOccIds()) {
                nextOverrides.set(
                    occId,
                    (nextOverrides.get(occId) ?? []).filter((object) => !removedIds.includes(object.itemId)),
                );
            }
            this.perOccurrenceOverrides.set(nextOverrides);
        }

        this.objectsForAllOccurrences.set(nextObjects);
        this.emitObjectChanges(previousObjects, nextObjects, "allOccurrences", "replace");
        this.updateModel();
        this.syncLocationCells();
    }

    async onAllOccurrencesPickerClose(selectedItems: S25ItemI[]): Promise<void> {
        const nextObjects = S25Util.array
            .forceArray(selectedItems)
            .map((item) => scheduleObjectFromItem(item, Item.Ids.Location));
        if (this.customizedOccIds().size > 0) {
            const confirmed = await AlertService.confirm(
                "Replace locations on every occurrence? Customized rows will use the new selection.",
            );
            if (!confirmed) {
                return;
            }
            this.customizedOccIds.set(new Set());
            this.perOccurrenceOverrides.set(new Map());
        }
        const previousObjects = this.objectsForAllOccurrences();
        this.objectsForAllOccurrences.set(nextObjects);
        this.emitObjectChanges(previousObjects, nextObjects, "allOccurrences", "replace");
        this.updateModel();
        this.syncLocationCells();
    }

    @Bind
    getTableRows(): Promise<Table.DataSourceResponse> {
        this.tableRevision();
        const rows = this.expandedOccurrences().map((row) => this.buildTableRow(row));
        return Promise.resolve({ rows });
    }

    private buildTableRow(row: OccurrenceScheduleRow): Table.Row {
        this.syncLocationCellForOccId(row.occId);

        return {
            id: row.occId,
            name: this.formatOccurrenceLabel(row),
            data: row,
            cells: {
                start: {
                    text: this.formatOccurrenceDt(row, "startDt"),
                    sortValue: S25Util.date.toS25ISODateTimeStr(row.startDt),
                },
                end: {
                    text: this.formatOccurrenceDt(row, "endDt"),
                    sortValue: S25Util.date.toS25ISODateTimeStr(row.endDt),
                },
                locations: this.getLocationCell(row.occId),
            },
        };
    }

    private getRowLocationSignals(occId: number): {
        selectedItems: WritableSignal<S25ItemI[]>;
        isCustomized: WritableSignal<boolean>;
    } {
        if (!this.rowSelectedItems.has(occId)) {
            this.rowSelectedItems.set(occId, signal<S25ItemI[]>([]));
            this.rowIsCustomized.set(occId, signal(false));
        }
        return {
            selectedItems: this.rowSelectedItems.get(occId)!,
            isCustomized: this.rowIsCustomized.get(occId)!,
        };
    }

    // Creates a new location cell if one isn't already defined
    private getLocationCell(occId: number): Table.Cell {
        let locationCell = this.rowLocationCells.get(occId);
        if (!locationCell) {
            const rowSignals = this.getRowLocationSignals(occId);
            locationCell = {
                component: S25OccurrenceScheduleLocationsCellComponent,
                inputs: {
                    pickerBean: this.getRowPickerBean(occId),
                    isCustomized: rowSignals.isCustomized,
                },
                twoWayInputs: {
                    selectedItems: rowSignals.selectedItems,
                },
                outputs: {
                    selectionChanged: (items: S25ItemI[]) => this.onOccurrenceSelectionChanged(occId, items),
                    pickerClose: (items: S25ItemI[]) => this.onOccurrencePickerClose(occId, items),
                },
            };
            this.rowLocationCells.set(occId, locationCell);
        }
        return locationCell;
    }

    private syncLocationCellForOccId(occId: number): void {
        const row = this.expandedOccurrences().find((occurrenceRow) => occurrenceRow.occId === occId);
        if (!row) {
            return;
        }

        const assignment = mergeDefaultAndPerOccurrence(
            [row],
            this.objectsForAllOccurrences(),
            this.perOccurrenceOverrides(),
            this.customizedOccIds(),
        )[0];
        const selectedItems = assignment.objects.map((scheduleObject) => scheduleItemFromObject(scheduleObject));
        const pickerBean = this.getRowPickerBean(occId);
        pickerBean.selectedItems = selectedItems;

        const rowSignals = this.getRowLocationSignals(occId);
        rowSignals.selectedItems.set(selectedItems);
        rowSignals.isCustomized.set(!objectsMatch(assignment.objects, this.objectsForAllOccurrences()));
        this.lastSyncedRowSelection.set(occId, [...selectedItems]);
    }

    private syncLocationCells(affectedOccIds?: number[]): void {
        const occurrenceRows = affectedOccIds?.length
            ? this.expandedOccurrences().filter((row) => affectedOccIds.includes(row.occId))
            : this.expandedOccurrences();
        for (const row of occurrenceRows) {
            this.syncLocationCellForOccId(row.occId);
        }
    }

    private getRowPickerBean(occId: number): MultiselectModelI {
        let pickerBean = this.rowPickerBeans.get(occId);
        if (!pickerBean) {
            pickerBean = { selectedItems: [] };
            this.rowPickerBeans.set(occId, pickerBean);
        }
        return pickerBean;
    }

    private onOccurrenceSelectionChanged(occId: number, selectedItems: S25ItemI[]): void {
        const previousAssignment = mergeDefaultAndPerOccurrence(
            [{ occId, startDt: new Date(), endDt: new Date() }],
            this.objectsForAllOccurrences(),
            this.perOccurrenceOverrides(),
            this.customizedOccIds(),
        )[0];
        const nextObjects = S25Util.array
            .forceArray(selectedItems)
            .map((item) => scheduleObjectFromItem(item, Item.Ids.Location));
        const defaultObjects = this.objectsForAllOccurrences();
        const nextOverrides = new Map(this.perOccurrenceOverrides());
        const nextCustomized = new Set(this.customizedOccIds());

        if (objectsMatch(nextObjects, defaultObjects)) {
            nextOverrides.delete(occId);
            nextCustomized.delete(occId);
        } else {
            nextOverrides.set(occId, nextObjects);
            nextCustomized.add(occId);
        }

        this.perOccurrenceOverrides.set(nextOverrides);
        this.customizedOccIds.set(nextCustomized);
        this.emitObjectChanges(previousAssignment.objects, nextObjects, "occurrence", "replace", occId);
        this.updateModel();
        this.syncLocationCellForOccId(occId);
    }

    private onOccurrencePickerClose(occId: number, selectedItems: S25ItemI[]): void {
        const lastSynced = this.lastSyncedRowSelection.get(occId);
        if (lastSynced && this.selectionItemsMatch(lastSynced, selectedItems)) {
            return;
        }
        this.onOccurrenceSelectionChanged(occId, selectedItems);
    }

    private selectionItemsMatch(left: S25ItemI[], right: S25ItemI[]): boolean {
        const leftIds = new Set(S25Util.array.forceArray(left).map((item) => Number(item.itemId)));
        const rightIds = new Set(S25Util.array.forceArray(right).map((item) => Number(item.itemId)));
        if (leftIds.size !== rightIds.size) {
            return false;
        }
        for (const itemId of leftIds) {
            if (!rightIds.has(itemId)) {
                return false;
            }
        }
        return true;
    }

    private syncPickerTimeModelFromScenario(): void {
        const picker = this.datePicker();
        if (!picker?.timeModel) {
            return;
        }
        const current = this.timeModel();
        picker.timeModel.evStartDt = current.evStartDt;
        picker.timeModel.evEndDt = current.evEndDt;
    }

    private syncAdHocOccsFromPicker(): void {
        const occurrences = this.datePicker()?.getOccurrences?.();
        if (occurrences?.length) {
            this.adHocOccs.set(S25Util.deepCopy(occurrences));
        }
    }

    private resolveRawOccurrences(): Occurrence[] {
        const profileCode = this.profileCode();
        if (profileCode === "dnr") {
            return [{ evStartDt: this.scenarioStart(), evEndDt: this.scenarioEnd() }];
        }
        if (profileCode === "adhoc") {
            return this.datePicker()?.getOccurrences?.() ?? this.adHocOccs();
        }
        return ProfileUtil.getOccurrencesFromProfileCode(this.timeModel(), profileCode);
    }

    private refreshExpandedOccurrences(
        emitDatesChange: boolean,
        trigger: OccurrenceScheduleDatesChangeContext["trigger"] = "patternChange",
    ): void {
        const previous = [...this.expandedOccurrences()];
        const rawOccurrences = this.resolveRawOccurrences();
        const next = expandOccurrenceRows(rawOccurrences, this.profileCode(), this.scenarioStart(), this.scenarioEnd());
        this.expandedOccurrences.set(next);
        this.pruneRowPickerBeans(next);

        if (emitDatesChange) {
            void this.emitDatesChange(trigger, previous, next);
        }
        this.tableRevision.update((revision) => revision + 1);
        void this.occurrenceTable()?.refresh();
    }

    private pruneRowPickerBeans(occurrences: OccurrenceScheduleRow[]): void {
        const validOccIds = new Set(occurrences.map((row) => row.occId));
        for (const occId of [...this.rowPickerBeans.keys()]) {
            if (!validOccIds.has(occId)) {
                this.rowPickerBeans.delete(occId);
            }
        }
        for (const occId of [...this.rowSelectedItems.keys()]) {
            if (!validOccIds.has(occId)) {
                this.rowSelectedItems.delete(occId);
                this.rowIsCustomized.delete(occId);
                this.rowLocationCells.delete(occId);
                this.lastSyncedRowSelection.delete(occId);
            }
        }
        const nextOverrides = new Map(this.perOccurrenceOverrides());
        const nextCustomized = new Set(this.customizedOccIds());
        for (const occId of [...nextOverrides.keys()]) {
            if (!validOccIds.has(occId)) {
                nextOverrides.delete(occId);
                nextCustomized.delete(occId);
            }
        }
        this.perOccurrenceOverrides.set(nextOverrides);
        this.customizedOccIds.set(nextCustomized);
    }

    private hydrateFromModel(value: OccurrenceScheduleValue | undefined): void {
        if (!value) {
            return;
        }
        this.profileCode.set(value.profileCode ?? "dnr");
        this.objectsForAllOccurrences.set([...(value.objectsForAllOccurrences ?? [])]);
        if (value.occurrences?.length) {
            this.expandedOccurrences.set([...value.occurrences]);
            const firstRow = value.occurrences[0];
            this.scenarioStart.set(firstRow.startDt);
            this.scenarioEnd.set(firstRow.endDt);
            this.adHocOccs.set(value.occurrences.map((row) => ({ evStartDt: row.startDt, evEndDt: row.endDt })));
        }
        const nextOverrides = new Map<number, OccurrenceScheduleObject[]>();
        const nextCustomized = new Set<number>();
        for (const assignment of value.assignments ?? []) {
            const defaultObjects = value.objectsForAllOccurrences ?? [];
            const differsFromDefault = !this.assignmentsEquivalent(assignment.objects, defaultObjects);
            if (differsFromDefault) {
                nextOverrides.set(assignment.occId, [...assignment.objects]);
                nextCustomized.add(assignment.occId);
            }
        }
        this.perOccurrenceOverrides.set(nextOverrides);
        this.customizedOccIds.set(nextCustomized);
    }

    private assignmentsEquivalent(left: OccurrenceScheduleObject[], right: OccurrenceScheduleObject[]): boolean {
        if (left.length !== right.length) {
            return false;
        }
        const leftIds = left.map((object) => object.itemId).sort((first, second) => first - second);
        const rightIds = right.map((object) => object.itemId).sort((first, second) => first - second);
        return leftIds.every((itemId, index) => itemId === rightIds[index]);
    }

    private updateModel(): void {
        this.model.set(
            buildScheduleValue(
                this.profileCode(),
                this.expandedOccurrences(),
                this.objectsForAllOccurrences(),
                this.perOccurrenceOverrides(),
                this.customizedOccIds(),
            ),
        );
    }

    private formatOccurrenceDt(row: OccurrenceScheduleRow, prop: "startDt" | "endDt"): string {
        const dateTime = row[prop];
        const format = this.userStore.dateTimeFormat();
        if (!format) {
            return dateTime.toLocaleString();
        }
        return S25Datefilter.transform(dateTime, format) as string;
    }

    private formatOccurrenceLabel(row: OccurrenceScheduleRow): string {
        return `${this.formatOccurrenceDt(row, "startDt")} – ${this.formatOccurrenceDt(row, "endDt")}`;
    }

    private buildCurrentValue(): OccurrenceScheduleValue {
        return buildScheduleValue(
            this.profileCode(),
            this.expandedOccurrences(),
            this.objectsForAllOccurrences(),
            this.perOccurrenceOverrides(),
            this.customizedOccIds(),
        );
    }

    private async emitDatesChange(
        trigger: OccurrenceScheduleDatesChangeContext["trigger"],
        previous: OccurrenceScheduleRow[],
        current: OccurrenceScheduleRow[],
    ): Promise<void> {
        const context: OccurrenceScheduleDatesChangeContext = {
            trigger,
            profileCode: this.profileCode(),
            previous,
            current,
        };
        this.datesChange.emit(context);
        await this.afterDatesChange()?.(context);
        this.updateModel();
    }

    private emitObjectChanges(
        previousObjects: OccurrenceScheduleObject[],
        nextObjects: OccurrenceScheduleObject[],
        scope: OccurrenceScheduleObjectChangeContext["scope"],
        action: OccurrenceScheduleObjectChangeContext["action"],
        occId?: number,
    ): void {
        const previousIds = new Set(previousObjects.map((object) => object.itemId));
        const nextIds = new Set(nextObjects.map((object) => object.itemId));
        for (const object of nextObjects) {
            if (!previousIds.has(object.itemId)) {
                this.emitObjectChange(scope, "add", object, occId);
            }
        }
        for (const object of previousObjects) {
            if (!nextIds.has(object.itemId)) {
                this.emitObjectChange(scope, "remove", object, occId);
            }
        }
        if (action === "replace" && nextObjects.length === 0 && previousObjects.length > 0) {
            this.emitObjectChange(scope, "replace", previousObjects[0], occId);
        }
    }

    private emitObjectChange(
        scope: OccurrenceScheduleObjectChangeContext["scope"],
        action: OccurrenceScheduleObjectChangeContext["action"],
        object: OccurrenceScheduleObject,
        occId?: number,
    ): void {
        const context: OccurrenceScheduleObjectChangeContext = {
            scope,
            occId,
            action,
            object,
            value: this.buildCurrentValue(),
        };
        this.objectChange.emit(context);
        void this.afterObjectChange()?.(context);
    }
}
